import { ReactNode, useEffect, useRef } from 'react';
import {
  Box,
  FelaCSS,
  FelaCSSWithCustomSelectors,
  TSetRef,
  TSizeValue,
  TSpacingStep,
} from '@bridebook/ui';
import { getThemedSpacing } from '@bridebook/ui/src/fela-utils/get-spacing-value';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';
import Slide from '../slide/slide';
import styles from './simple-slider-mobile.style';

interface IProps {
  slides: { key: string }[];
  loader?: ReactNode;
  lazyLoad?: boolean;
  carouselTitle?: string;
  setRef?: TSetRef | ReturnType<typeof useRef>;
  slideWidth?: TSizeValue;
  containerStyles?: FelaCSS;
  slideStyles?: FelaCSSWithCustomSelectors;
  onTouchEnd?: () => void;
  onSlide?: (index: number) => void;
  noPadding?: boolean;
  gap?: TSpacingStep;
}

const DEBOUNCE_TIME_MS = 500;

const SimpleSliderMobile = ({
  slides = [],
  loader,
  lazyLoad,
  setRef,
  slideWidth = 282,
  containerStyles,
  slideStyles = {},
  onTouchEnd,
  onSlide,
  noPadding,
  gap = 4,
}: IProps) => {
  // Debounce timer to detect when momentum scrolling has fully settled
  const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the timer on unmount to avoid calling onSlide after the component is gone
  useEffect(
    () => () => {
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    },
    [],
  );

  const handleScroll = (scrollLeft: number) => {
    // Reset the timer on every scroll event — fires only after scrolling stops
    if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    if (!onSlide) return;
    scrollEndTimerRef.current = setTimeout(() => {
      // Each slide occupies slideWidth + gap pixels in the scroll container
      const slideWidthPx = parseFloat(String(slideWidth));
      const gapPx = parseFloat(String(!Array.isArray(gap) ? getThemedSpacing(gap) : 0));
      const stepPx = slideWidthPx + (isNaN(gapPx) ? 0 : gapPx);
      if (!isNaN(slideWidthPx) && stepPx > 0) {
        // Index - how many full slide + gap steps fit into the current scroll position, rounded to nearest
        onSlide(Math.round(scrollLeft / stepPx));
      }
    }, DEBOUNCE_TIME_MS);
  };

  return (
    <Box
      onTouchEnd={onTouchEnd}
      onScroll={(e) => {
        handleScroll((e.currentTarget as unknown as HTMLElement).scrollLeft);
      }}
      pb={0}
      style={containerStyles ? { ...styles.wrapper, ...containerStyles } : styles.wrapper}
      data-name="simple-slider-mobile"
      setRef={setRef}>
      {slides.map((item, itemIndex) => (
        <Box
          pr={gap}
          w={slideWidth}
          style={mergeStyles([styles.slideWrap(noPadding), slideStyles])}
          key={item.key || `index_${itemIndex}`}>
          <Slide loader={loader} lazyLoad={lazyLoad}>
            {item}
          </Slide>
        </Box>
      ))}
    </Box>
  );
};

export default SimpleSliderMobile;
